import { UPDATE_MY_ORGANIZATION } from './mutations/organizations/update.my.organizations';
import { ACCEPT_STAFF_INVITE } from './mutations/staff/accept.staff.invite';
import { INVITE_STAFF } from './mutations/staff/invite.staff';
import { REJECT_STAFF_INVITE } from './mutations/staff/reject.staff.invite';
import { InviteStaffInput, InviteStaffResponse } from './mutations/staff/types';
import { api, ApiTags } from './base';
import { GET_STAFF_OUTGOING_INVITES } from './queries/staff/staff.outgoing.invites';
import { StaffInvitesListResponse } from './queries/staff/types';
import { PoaDraft } from './types';
import { Employee, StaffInvite } from './types/staff';
import {
  StaffByIdQuery,
  StaffIncomingInvitesCountQuery,
  StaffIncomingInvitesQuery,
  StaffListQuery,
  StaffOutgoingInvitesCountQuery,
  StaffOutgoingInvitesQuery,
  StaffPoasListQuery,
  useStaffByIdQuery,
  useStaffIncomingInvitesCountQuery,
  useStaffIncomingInvitesQuery,
  useStaffListQuery,
  useStaffOutgoingInvitesCountQuery,
  useStaffOutgoingInvitesQuery,
  useStaffPoasListQuery as useGeneratedStaffPoasListQuery,
} from './generated';
import { unwrapQuery } from './unwrap';

export const useGetStaffListQuery = (
  { search, role }: { search: string; role?: string },
  options?: Parameters<typeof useStaffListQuery>[1],
) =>
  unwrapQuery(
    useStaffListQuery(
      {
        searchText: search?.length ? search : null,
        roles: role ? [role as never] : null,
      },
      options,
    ),
    (raw: StaffListQuery | undefined) =>
      raw?.coreEmployees?.query?.employees as Employee[] | undefined,
  );

export const useGetStaffByIdQuery = (...args: Parameters<typeof useStaffByIdQuery>) =>
  unwrapQuery(
    useStaffByIdQuery(...args),
    (raw: StaffByIdQuery | undefined) => raw?.coreEmployees?.get?.[0] as Employee | undefined,
  );

export const useGetStaffIncomingInvitationsListQuery = (
  ...args: Parameters<typeof useStaffIncomingInvitesQuery>
) =>
  unwrapQuery(
    useStaffIncomingInvitesQuery(...args),
    (raw: StaffIncomingInvitesQuery | undefined) =>
      raw?.coreEmployeeInvitations?.query?.invites as StaffInvite[] | undefined,
  );

export const useGetStaffOutgoingInvitationsListQuery = (
  ...args: Parameters<typeof useStaffOutgoingInvitesQuery>
) =>
  unwrapQuery(
    useStaffOutgoingInvitesQuery(...args),
    (raw: StaffOutgoingInvitesQuery | undefined) =>
      raw?.coreEmployeeInvitations?.query?.invites as StaffInvite[] | undefined,
  );

export const useGetStaffIncomingInvitationsCountQuery = (
  ...args: Parameters<typeof useStaffIncomingInvitesCountQuery>
) =>
  unwrapQuery(
    useStaffIncomingInvitesCountQuery(...args),
    (raw: StaffIncomingInvitesCountQuery | undefined) => raw?.coreEmployeeInvitations?.count,
  );

export const useGetStaffOutgoingInvitationsCountQuery = (
  ...args: Parameters<typeof useStaffOutgoingInvitesCountQuery>
) =>
  unwrapQuery(
    useStaffOutgoingInvitesCountQuery(...args),
    (raw: StaffOutgoingInvitesCountQuery | undefined) => raw?.coreEmployeeInvitations?.count,
  );

export const useStaffPoasListQuery = (
  ...args: Parameters<typeof useGeneratedStaffPoasListQuery>
) =>
  unwrapQuery(
    useGeneratedStaffPoasListQuery(...args),
    (raw: StaffPoasListQuery | undefined) => raw?.corePOA?.query?.poas as PoaDraft[] | undefined,
  );

/**
 * Мутации остались рукописными: их результат читают из промиса триггера
 * (`staffInvite` возвращает id приглашения), а сгенерированный хук отдал бы конверт.
 */
export const staffApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Оставлен рукописным: `AddStaff` читает результат через `trigger().unwrap()`,
    // а сгенерированный хук вернул бы туда весь GraphQL-конверт.
    getStaffOutgoingInvitationsList: builder.query<StaffInvite[], void>({
      query: () => ({
        document: GET_STAFF_OUTGOING_INVITES,
      }),
      transformResponse: (rawResponse: StaffInvitesListResponse) =>
        rawResponse?.coreEmployeeInvitations?.query.invites,
      providesTags: [ApiTags.StaffInviteList],
    }),

    updateStaff: builder.mutation<unknown, { input: unknown }>({
      query: ({ input }) => ({
        document: UPDATE_MY_ORGANIZATION,
        variables: { input },
      }),
      transformResponse: (rawResponse: { coreOrganizations?: { update?: unknown } }) =>
        rawResponse?.coreOrganizations?.update,
      invalidatesTags: [ApiTags.StaffList],
    }),

    staffInvite: builder.mutation<string | undefined, InviteStaffInput>({
      query: ({ participantData, role, type, position }) => ({
        document: INVITE_STAFF,
        variables: { participantData, role, type, position },
      }),
      transformResponse: (rawResponse: InviteStaffResponse) =>
        rawResponse?.coreEmployeeInvitations?.invite,
      invalidatesTags: [ApiTags.StaffInviteList, ApiTags.StaffList],
    }),

    confirmStaffInvite: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        document: ACCEPT_STAFF_INVITE,
        variables: { invitationId: id },
      }),
      invalidatesTags: [ApiTags.StaffInviteList, ApiTags.StaffList],
    }),

    rejectStaffInvite: builder.mutation<unknown, { id: string }>({
      query: ({ id }) => ({
        document: REJECT_STAFF_INVITE,
        variables: { invitationId: id },
      }),
      invalidatesTags: [ApiTags.StaffInviteList, ApiTags.StaffList],
    }),
  }),
});

export const {
  useLazyGetStaffOutgoingInvitationsListQuery,
  useUpdateStaffMutation,
  useStaffInviteMutation,
  useRejectStaffInviteMutation,
  useConfirmStaffInviteMutation,
} = staffApi;
